import {Injectable} from '@angular/core';
import {Color, Geometry, Mesh, Vector2} from 'three';
import {CameraService} from '../Scene/camera.service';
import {ContainerService} from '../../Utils/container.service';
import {LoaderService} from '../Scene/loader.service';
import {TextureSizeService} from '../Calculation/texture-size.service';
import {RenderService} from '../Scene/render.service';
import {ColorCodingService} from './color-coding.service';
import {MarkingType} from './markingType';
import {EditorControlService} from '../../API/Public/editor-control.service';
import {MouseControlsService} from '../Control/mouse-controls.service';
import {CanvasEvent} from '../Control/controls';
import {PaintService} from './paint.service';
import {PolyPaintService} from './poly-paint.service';

@Injectable({
    providedIn: 'root'
})
export class TexturePaintService {

    public readonly lassoControl = 'drawOnTexture';
    public readonly infoControl = 'info';

    private _assessmentCounter: number;
    private _mapCpy: Map<number, MarkingType>;

    private _selectionInfo: MarkingType;

    constructor(private paintService: PaintService,
                private polyService: PolyPaintService,
                private cameraService: CameraService,
                private containerService: ContainerService,
                private loaderService: LoaderService,
                private textureSizeService: TextureSizeService,
                private renderService: RenderService,
                private colorCodingService: ColorCodingService,
                private mouseService: MouseControlsService,
                private editorControlService: EditorControlService) {
        this._assessmentCounter = 0;
        this.registerControls();
    }

    get selectionInfo(): MarkingType {
        return this._selectionInfo;
    }

    set selectionInfo(value: MarkingType) {
        this._selectionInfo = value;
    }

    /**
     *
     * @param point
     * @param poly
     *
     * Checks if Point is in lasso
     *
     */
    private static pointInPolygon(point: Vector2, poly) {
        const {x, y} = point;

        let inside = false;
        for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
            const {xi, yi} = poly[i];
            const {xj, yj} = poly[j];

            const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) {
                inside = !inside;
            }
        }

        return inside;
    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    /**
     * marks (or erases) pixel in texture
     * depending on camera distance neighbours might also be markes
     */
    public markPointInTexture(p: Set<number>, texture: HTMLCanvasElement, erase: boolean, markType: MarkingType, neighbours = true) {
        const ctx = texture.getContext('2d');
        const imgd = ctx.getImageData(0, 0, this.containerService.textureRes, this.containerService.textureRes);
        const pix = imgd.data;

        markType.assessmentIndex = this._assessmentCounter;
        // markType.comment = 'Assessment belongs to: ' + this._assessmentCounter;
        this._assessmentCounter++;

        const mesh = <Mesh>this.containerService.renderGroup.children[0];
        const geo = <Geometry>mesh.geometry;
        geo.computeFaceNormals();
        p.forEach((key: number) => {
            // TODO: delete from set when erasing
            const {x, y} = this.mapIndex(key);
            // TODO if front and back painting is activated checkCamera Angel should be disabled!!!
            // if (this.checkCameraAngle(mesh, geo, k)) {
            if (neighbours) {
                // TODO: try with distance to camera
                if (Math.abs(this.cameraService.camera.position.z) <= 60) {
                    this.markPixelNeighbourhood(x, y, 4, pix, markType, erase);
                } else {
                    this.markPixelNeighbourhood(x, y, 8, pix, markType, erase);
                }
            } else {
                this.markPixel(x, y, pix, markType, erase);
            }

        });
        ctx.putImageData(imgd, 0, 0);


        this.containerService.texture.needsUpdate = true;
        this.textureSizeService.change = true;
    }

    /**
     * resets texture painting
     */
    public resetTexure() {
        this.textureSizeService.reset();
        this.loaderService.loadTexure();
    }


    public deactivateInfo() {
        if (this._mapCpy) {
            const ctx = this.containerService.textureCanvas.getContext('2d');
            const imgd = ctx.getImageData(0, 0, this.containerService.textureRes, this.containerService.textureRes);
            const pix = imgd.data;
            // find all pixels which belong to this assessment;
            this.highlightBurn(this._mapCpy, pix, true);
            this._mapCpy = null;
            ctx.putImageData(imgd, 0, 0);
            this.containerService.texture.needsUpdate = true;
            this.textureSizeService.change = true;
            this._selectionInfo = null;
            this.renderService.render();
        }
    }

    public getBurnInfo(intersect: any) {
        const faces = this.textureSizeService.faceMap[intersect.faceIndex];
        const texelInfo = this.findFaceInfo(faces);
        if (texelInfo) {
            this._selectionInfo = texelInfo;
            const ctx = this.containerService.textureCanvas.getContext('2d');
            const imgd = ctx.getImageData(0, 0, this.containerService.textureRes, this.containerService.textureRes);
            const pix = imgd.data;
            // find all pixels which belong to this assessment;
            const completeInfo = this.findAllTexelsForAssessment(texelInfo.assessmentIndex);
            if (this._mapCpy) {
                this.highlightBurn(this._mapCpy, pix, true);
                this.highlightBurn(completeInfo, pix, false);
                this._mapCpy = completeInfo;
            } else {
                this.highlightBurn(completeInfo, pix, false);
                this._mapCpy = completeInfo;
            }
            ctx.putImageData(imgd, 0, 0);
            this.containerService.texture.needsUpdate = true;
            this.textureSizeService.change = true;
        }
    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    // -- event controls ---------------------

    private registerControls() {
        this.editorControlService.registerControl(this.lassoControl, {
            events: new Map([
                [CanvasEvent.Down, event => { this.paintService.startPaintFromEvent(event); }],
                [CanvasEvent.Move, event => { this.paintService.paintForEvent(event, false); }],
                [CanvasEvent.Up, this.finishLassoPaint]
            ])
        });
        this.editorControlService.registerControl(this.infoControl, {
            events: new Map([
                [CanvasEvent.Down, this.getBurnInfoFromEvent.bind(this)]
            ]),

            finish: this.deactivateInfo.bind(this),

            pan: false,
            zoom: true,
            rotate: true,
            controlMode: false
        });
    }

    private finishLassoPaint() {
        this.paintService.closePoly();
        this.paintService.drawToScene(true);
        // this.renderColorpaintImg();

        const model = <Mesh> this.containerService.renderGroup.children[0];
        this.polyService.lassoSelection(model, this.paintService.rawpoints);
        this.lassoSelection(model, this.paintService.rawpoints);
        this.paintService.clearLassoPoints();
    }

    private getBurnInfoFromEvent(event: MouseEvent) {
        if (event.buttons !== 2 && this.containerService.loadingStatus === 1) {
            const mouse = this.mouseService.getCanvasMouse(event);
            const model = <Mesh>this.containerService.renderGroup.children[0];
            const intersects = this.mouseService.getMouseModelIntersection(mouse, model);

            if (intersects.length > 0) {
                this.getBurnInfo(intersects[0]);
                this.renderService.render();
            }
        }
    }

    // --------------------------------------

    /**
     * starts lasso Selection in Texture
     */
    private lassoSelection(modelMesh: Mesh, selPoints2D: any, erase: boolean = false) {
        const indexSet = new Set<number>();
        this.colorCodingService.initBufferTexture();
        const p = this.colorCodingService.getCheckarea(selPoints2D, 150);
        const x = p[0];
        const y = p[1];
        const w = p[2];
        const h = p[3];

        const markType = new MarkingType();
        markType.color = this.paintService.paintColor.color;
        this.colorCodingService.renderColorpaintImg(x, y, w, h, true);
        for (let i = 0; i <= (w - x) * 4; i += 4) {
            for (let j = 0; j <= (h - y) * 4; j += 4) {
                const c = this.colorCodingService.checkVisibilityoverColors(i, j, w - x);
                if (c != null) {
                    const position: Vector2 = this.colorCodingService.transformToCenterCoord((x + (i / 4)), (y + (j / 4)));
                    if (TexturePaintService.pointInPolygon(position, selPoints2D)) {
                        const index = this.colorCodingService.getColorIndexTexture(c);
                        indexSet.add(index);

                        if (erase) {
                            this.textureSizeService.pixelSet.delete(index);
                        } else {
                            this.textureSizeService.pixelSet.set(index, markType);
                        }
                    }
                }
            }
        }
        this.markPointInTexture(indexSet, this.containerService.textureCanvas, false, markType, false);
        this.containerService.texture.needsUpdate = true;
        this.textureSizeService.change = true;
        this.renderService.render();

    }

    /**
     * mark/erase pixel at position x,y with given color
     * @param x
     * @param y
     * @param pix
     * @param markType
     * @param erase
     */
    private markPixel(x: number, y: number, pix, markType: MarkingType, erase: boolean) {
        const j = this.mapTextureCoordinates(x, y);
        const color = markType.color.clone().multiplyScalar(255);
        pix[j] = color.r; // red
        pix[j + 1] = color.g; // green
        pix[j + 2] = color.b; // blue
        if (erase) {
            this.textureSizeService.pixelSet.delete(j);
        } else {
            const set = this.textureSizeService.pixelSet;
            const value = set.get(j);
            if (set.has(j) && value.color === markType.color) {
                markType.assessmentIndex = value.assessmentIndex;
            }
            this.textureSizeService.pixelSet.set(j, markType);
        }
    }

    private markPixelNeighbourhood(centerX: number, centerY: number, size: number, pix, markType: MarkingType, erase: boolean) {
        for (let x = -size; x <= size; x += 4) {
            for (let y = -size; y <= size; y += 4) {
                this.markPixel(centerX + x, centerY + y, pix, markType, erase);
            }
        }
    }

    /**
     * map index to x and y
     * @param index
     */
    private mapIndex(index: number) {
        const x = index % this.containerService.textureRes;
        const y = Math.floor(index / this.containerService.textureRes);
        return {x, y};
    }

    /**
     * converts x and y to texel index
     * @param x
     * @param y
     */
    private mapTextureCoordinates(x: number, y: number) {
        return x + this.containerService.textureRes * y;
    }

    /**
     * TODO should may be removed
     * @param index
     * @param pix
     */
    private highlightBurn(index: Map<number, MarkingType>, pix, toggle: boolean) {
        let color = new Color(255, 255, 0);
        if (toggle) {
            const help = index.values().next().value;
            color = help.color.clone();
            color.multiplyScalar(255);
        }
        index.forEach((value, key) => {

            pix[key] = color.r; // red
            pix[key + 1] = color.g; // green
            pix[key + 2] = color.b; // blue
        });

    }

    private findFaceInfo(faces: any) {
        for (const f of faces) {
            const info = this.textureSizeService.pixelSet.get(f.index);
            if (info) {
                return info;
            }

        }
        return null;
    }

    private findAllTexelsForAssessment(id: number): any {
        const result = new Map<number, MarkingType>();
        this.textureSizeService.pixelSet.forEach((value, key) => {
            if (value.assessmentIndex === id) {
                result.set(key, value);
            }
        });
        return result;
    }


    // TODO: remove?
    // /**
    //  * check if face direction to camera
    //  * @param mesh
    //  * @param geo
    //  * @param index
    //  */
    // private checkCameraAngle(mesh: Mesh, geo: Geometry, index: number) {
    //     if (index) {
    //         return true;
    //         const face = geo.faces[index];
    //         const normal = face.normal;
    //         const camera = this.cameraService.camera;
    //         const dirToCamera = camera.position.clone().sub(mesh.position);
    //         dirToCamera.normalize();
    //         const target = new Vector3(0, 0, 0);
    //         camera.getWorldDirection(target);
    //         target.negate();
    //         const vn1 = face.vertexNormals[0];
    //         const vn2 = face.vertexNormals[1];
    //         const vn3 = face.vertexNormals[2];
    //         const v1 = geo.vertices[face.a];
    //         const v2 = geo.vertices[face.b];
    //         const v3 = geo.vertices[face.c];
    //         // if (target.dot(normal) < 0) {
    //         //     return  true;
    //         // } else {
    //         //     return false;
    //         // }
    //         const angleValue = normal.angleTo(target);
    //         const angle = (45 * Math.PI) / 180;
    //         // const angle = 1.250;
    //         // const angle = 0.5;
    //         // return  (vn1.angleTo(target) < angle && vn2.angleTo(target) < angle && vn3.angleTo(target) < angle);
    //         return angleValue < angle;
    //         const cameraVector = (new Vector3(0, 0, -1)).applyQuaternion(camera.quaternion);
    //         return (normal.angleTo(cameraVector) < angle);
    //
    //     }
    //     return false;
    //
    // }

}
